/**
 * RequirePermission Component
 *
 * Wrapper component for permission-based access control.
 * Checks if the current user has the required permission before rendering children.
 * If not, redirects to /unauthorized or shows fallback component.
 *
 * Usage:
 * <RequirePermission resource="substabelecidos" action="manage">
 *   <SubstabelecidosAdmin />
 * </RequirePermission>
 */

import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { rbacService } from "@/services/rbacService";
import { Loader2 } from "lucide-react";

interface RequirePermissionProps {
  /** Resource to check permission for (e.g., "substabelecidos", "faq") */
  resource: string;
  /** Action to check permission for (e.g., "read", "create", "update", "delete", "manage") */
  action: string;
  /** Content to render if user has permission */
  children: React.ReactNode;
  /** Optional fallback to render if user doesn't have permission (default: redirect to /unauthorized) */
  fallback?: React.ReactNode;
  /** Optional loading component (default: spinner) */
  loadingComponent?: React.ReactNode;
}

export const RequirePermission: React.FC<RequirePermissionProps> = ({
  resource,
  action,
  children,
  fallback,
  loadingComponent,
}) => {
  const { user, isAuthenticated } = useAuth();
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkPermission = async () => {
      // Not authenticated - no permission
      if (!isAuthenticated || !user) {
        setHasPermission(false);
        setIsLoading(false);
        return;
      }

      try {
        // Check permission via RBAC service
        const result = await rbacService.checkPermission(resource, action);
        // result is { has_permission: boolean, granted_by_roles?: string[] }
        setHasPermission(result.has_permission);
      } catch (error) {
        console.error("[RequirePermission] Permission check failed:", error);
        // On error, deny access by default (fail-safe)
        setHasPermission(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkPermission();
  }, [user, isAuthenticated, resource, action]);

  // Loading state
  if (isLoading) {
    return (
      loadingComponent || (
        <div className="flex items-center justify-center h-64">
          <div className="text-center space-y-3">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="text-sm text-muted-foreground">Verificando permissões...</p>
          </div>
        </div>
      )
    );
  }

  // No permission
  if (!hasPermission) {
    return fallback || <Navigate to="/unauthorized" replace />;
  }

  // Has permission - render children
  return <>{children}</>;
};

/**
 * Hook version for conditional rendering within components
 *
 * Usage:
 * const canManage = usePermission('substabelecidos', 'manage');
 *
 * return (
 *   <div>
 *     {canManage && <Button>Delete</Button>}
 *   </div>
 * );
 */
export const usePermission = (resource: string, action: string): boolean | null => {
  const { user, isAuthenticated } = useAuth();
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);

  useEffect(() => {
    const checkPermission = async () => {
      if (!isAuthenticated || !user) {
        setHasPermission(false);
        return;
      }

      try {
        const result = await rbacService.checkPermission(resource, action);
        // result is { has_permission: boolean, granted_by_roles?: string[] }
        setHasPermission(result.has_permission);
      } catch (error) {
        console.error("[usePermission] Permission check failed:", error);
        setHasPermission(false);
      }
    };

    checkPermission();
  }, [user, isAuthenticated, resource, action]);

  return hasPermission;
};

export default RequirePermission;
